import { FunctionComponent, useState } from 'react'
import { Close, Comment, ContentCopy } from '@mui/icons-material'
import styles from './closingDay.module.scss'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Daycare } from '@/types/Daycare'
import { ClosingDay } from '@/types/ClosingDays'
import EditClosingDayDialog from './Dialog/editClosingDayDialog'
import ConfirmDeleteClosingDayDialog from './Dialog/confirmDeleteClosingDayDialog'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper'
import { format, isAfter, isEqual, parse, startOfDay } from 'date-fns'
import DuplicateClosingDayDialog from '../Dialog/duplicateClosingDay.tsx'

interface ClosingDayProps {
    closingDay: ClosingDay
    parent?: boolean
    canUpdate: boolean
    daycare?: Daycare
    closingDays?: ClosingDay[]
    refetchAllEvents: () => Promise<void>
}

const ClosingDayRow: FunctionComponent<ClosingDayProps> = ({
    closingDay,
    parent = false,
    canUpdate,
    daycare,
    closingDays,
    refetchAllEvents
}: ClosingDayProps) => {
    const { t } = useTranslation()

    const [isEditDialogOpen, setEditDialogOpen] = useState<boolean>(false)
    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false)

    const [isDuplicateClosingDayDialogOpen, setDuplicateClosingDayDialogOpen] = useState<boolean>(false)

    return (
        <div className={`${styles.EventDayPlacement} ${parent && styles.EventDayParent}`}>
            <div className={styles.TitleContainer}>
                <Close className={`${parent ? styles.IconParent : styles.IconDaycare}`} />
                <div className={styles.TextContainer}>
                    <p className={styles.Title}>{closingDay.closingDaysReason}</p>
                    <p className={styles.Date}>
                        {t('closingDayOf') +
                            ' ' +
                            format(parse(closingDay.startDate, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                                locale: getLocalFromLanguage()
                            })}
                        {closingDay.startDate !== closingDay.endDate &&
                            ' ' +
                                t('until').toLowerCase() +
                                ' ' +
                                format(parse(closingDay.endDate, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                                    locale: getLocalFromLanguage()
                                })}
                    </p>
                </div>
            </div>
            {closingDay.closingDaysMessage && (
                <>
                    <div className={styles.BreakLine}></div>
                    <div className={styles.Container}>
                        <Comment className={`${parent ? styles.IconParent : styles.IconDaycare}`} />
                        <p className={styles.Text}>{closingDay.closingDaysMessage}</p>
                    </div>
                </>
            )}
            {canUpdate && !parent && (
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('duplicate')}
                        customIcon={<ContentCopy />}
                        onClick={() => setDuplicateClosingDayDialogOpen(true)}
                        className={styles.Button}
                    />
                    {isAfter(parse(closingDay.endDate, 'dd-MM-yyyy', new Date()), new Date()) ||
                        (isEqual(
                            startOfDay(parse(closingDay.endDate, 'dd-MM-yyyy', new Date())),
                            startOfDay(new Date())
                        ) && (
                            <ButtonTextIcon
                                type={'edit'}
                                onClick={() => setEditDialogOpen(true)}
                                className={styles.Button}
                            />
                        ))}
                    <ButtonTextIcon
                        type={'delete'}
                        onClick={() => setDeleteDialogOpen(true)}
                        className={styles.Button}
                    />
                </div>
            )}
            {isEditDialogOpen && (
                <EditClosingDayDialog
                    closingDay={closingDay}
                    daycareId={closingDay.daycareId}
                    open={isEditDialogOpen}
                    closingDays={closingDays}
                    handleClose={() => setEditDialogOpen(false)}
                    refetchAllEvents={refetchAllEvents}
                />
            )}
            {isDeleteDialogOpen && (
                <ConfirmDeleteClosingDayDialog
                    open={isDeleteDialogOpen}
                    handleClose={() => setDeleteDialogOpen(false)}
                    refetchAllEvents={refetchAllEvents}
                    closingDay={closingDay}
                    daycareId={closingDay.daycareId}
                />
            )}
            {isDuplicateClosingDayDialogOpen && (
                <DuplicateClosingDayDialog
                    closingDay={closingDay}
                    daycare={daycare}
                    open={isDuplicateClosingDayDialogOpen}
                    handleClose={() => setDuplicateClosingDayDialogOpen(false)}
                    refetchAllEvents={refetchAllEvents}
                />
            )}
        </div>
    )
}

export default ClosingDayRow
